/**
 * GoogleAuthCallback — handles the native verqo Google OAuth redirect
 * (Feb 2026 — replaces the previous Emergent-managed flow).
 *
 * Google bounces the browser back to `{origin}/auth/callback?code=…&state=…`
 * after the user approves the consent screen. We post both to
 * `/auth/google/callback` which validates the CSRF state, exchanges the
 * code with Google, verifies the id_token, and issues a verqo JWT.
 *
 * If anything goes wrong (state expired, code reused, network blip),
 * we show a friendly error and redirect back to /login after a beat.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { postLoginUrl } from "../lib/hostScope";
import { VerqoLockup } from "../components/VerqoLockup";

export default function GoogleAuthCallback() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const handled = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const run = async () => {
      // Google appends ?code=…&state=… on success, or ?error=… on
      // user-denied / config errors. Surface both gracefully.
      const code  = params.get("code");
      const state = params.get("state");
      const googleErr = params.get("error");

      if (googleErr) {
        setError(
          googleErr === "access_denied"
            ? "You declined the Google consent screen. Please try again to sign in."
            : `Google returned an error: ${googleErr}.`,
        );
        setTimeout(() => nav("/login", { replace: true }), 3500);
        return;
      }
      if (!code || !state) {
        setError("No sign-in code in the callback URL — please start sign-in again.");
        setTimeout(() => nav("/login", { replace: true }), 3000);
        return;
      }

      try {
        const { data } = await api.post(
          "/auth/google/callback",
          { code, state },
          { withCredentials: true },
        );
        if (data.token) localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data));
        setUser?.(data);
        toast.success(`Welcome${data.name ? `, ${data.name.split(" ")[0]}` : ""}`);
        // Clear the code+state from history so a refresh doesn't re-trigger.
        window.history.replaceState(null, "", window.location.pathname);
        const url = postLoginUrl(data.role, {});
        if (url.startsWith("http://") || url.startsWith("https://")) {
          window.location.replace(url);
        } else {
          nav(url, { replace: true });
        }
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const code2 = detail?.code;
        const msg =
          detail?.message ||
          (typeof detail === "string" ? detail : null) ||
          "Sign-in didn't complete. Please try again.";
        setError(msg);
        if (code2 === "signup_closed") {
          setTimeout(() => nav("/waitlist", { replace: true }), 2500);
        } else {
          setTimeout(() => nav("/login", { replace: true }), 3000);
        }
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center" data-testid="google-auth-callback">
        <div className="mb-6 flex justify-center">
          <VerqoLockup className="h-7" />
        </div>
        {!error ? (
          <div className="space-y-3" data-testid="google-callback-loading">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-foreground/50" />
            <h1 className="display text-2xl tracking-tight">Signing you in…</h1>
            <p className="text-[13px] text-foreground/55">
              Verifying your Google account. This usually takes a second.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="google-callback-error">
            <div className="w-10 h-10 mx-auto rounded-full bg-rose-50 text-rose-700 grid place-items-center">
              <AlertCircle className="w-5 h-5" />
            </div>
            <h1 className="display text-2xl tracking-tight">Sign-in didn&apos;t complete</h1>
            <p className="text-[13px] text-foreground/65 max-w-sm mx-auto leading-relaxed">
              {error}
            </p>
            <p className="text-[11.5px] text-foreground/45 mt-1">
              Redirecting in a moment…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
